/**
 * labels.ts — Libellés dynamiques de l'application
 *
 * Pour changer le contexte métier, modifier la valeur de `activeConfig` :
 *   - 'agence'      → Locataire / Propriétaire / Contrat de location
 *   - 'commercial'  → Client / Fournisseur / Commande
 *
 * Ajouter un nouveau contexte :
 *   1. Créer un objet qui respecte le type `AppLabels`
 *   2. L'ajouter dans `configs`
 *   3. Changer `activeConfig`
 */

// ─── Type de base ────────────────────────────────────────────────────────────

export interface AppLabels {
  // Tiers
  tiers: string;
  tiersPluriel: string;
  client: string;
  clientPluriel: string;
  fournisseur: string;
  fournisseurPluriel: string;
  employe: string;
  employePluriel: string;

  // Fournisseur principal d'un article (0143) — phrases complètes : l'accord
  // (le / la, nouveau / nouvelle) dépend du terme de chaque contexte
  fournisseurActuel: string;
  nouveauFournisseur: string;
  sansFournisseur: string;
  retirerFournisseur: string;
  affecterFournisseur: string;
  fournisseurAAffecter: string;
  fournisseurParDefaut: string;
  fournisseurMisAJour: string;

  // Contrat
  contrat: string;
  contratPluriel: string;
  numeroContrat: string;

  // Bien / Produit
  bien: string;
  bienPluriel: string;
  groupeBien: string;

  // Document commercial
  documentVente: string;
  documentVentePluriel: string;
  ligneDocument: string;

  // Paiement / Règlement
  reglement: string;
  reglementPluriel: string;
  loyer: string;

  // Modules / Titres de pages
  moduleFacturation: string;
  gestionContrat: string;
  gestionBiens: string;
  gestionTiers: string;
  gestionArticles: string;
  gestionCaisse: string;
}

// ─── Configurations disponibles ──────────────────────────────────────────────

const agenceImmobiliere: AppLabels = {
  tiers: 'Tiers',
  tiersPluriel: 'Tiers',
  client: 'Locataire',
  clientPluriel: 'Locataires',
  fournisseur: 'Propriétaire',
  fournisseurPluriel: 'Propriétaires',
  employe: 'Employé',
  employePluriel: 'Employés',

  fournisseurActuel: 'Propriétaire actuel',
  nouveauFournisseur: 'Nouveau propriétaire',
  sansFournisseur: 'Sans propriétaire',
  retirerFournisseur: 'Retirer le propriétaire',
  affecterFournisseur: 'Affecter le propriétaire',
  fournisseurAAffecter: 'Propriétaire à affecter',
  fournisseurParDefaut: 'Propriétaire par défaut',
  fournisseurMisAJour: 'propriétaire mis à jour',

  contrat: 'Contrat de location',
  contratPluriel: 'Contrats de location',
  numeroContrat: 'N° Contrat',

  bien: 'Bien',
  bienPluriel: 'Biens',
  groupeBien: 'Groupe de biens',

  documentVente: 'Document de vente',
  documentVentePluriel: 'Documents de vente',
  ligneDocument: 'Ligne document',

  reglement: 'Règlement',
  reglementPluriel: 'Règlements',
  loyer: 'Loyer',

  moduleFacturation: 'Module de facturation',
  gestionContrat: 'Gestion contrats de location',
  gestionBiens: 'Gestion des biens',
  gestionTiers: 'Gestion des tiers',
  gestionArticles: 'Gestion des articles',
  gestionCaisse: 'Gestion caisse',
};

const commercial: AppLabels = {
  tiers: 'Tiers',
  tiersPluriel: 'Tiers',
  client: 'Client',
  clientPluriel: 'Clients',
  fournisseur: 'Fournisseur',
  fournisseurPluriel: 'Fournisseurs',
  employe: 'Employé',
  employePluriel: 'Employés',

  fournisseurActuel: 'Fournisseur actuel',
  nouveauFournisseur: 'Nouveau fournisseur',
  sansFournisseur: 'Sans fournisseur',
  retirerFournisseur: 'Retirer le fournisseur',
  affecterFournisseur: 'Affecter le fournisseur',
  fournisseurAAffecter: 'Fournisseur à affecter',
  fournisseurParDefaut: 'Fournisseur par défaut',
  fournisseurMisAJour: 'fournisseur mis à jour',

  contrat: 'contrat',
  contratPluriel: 'contrats',
  numeroContrat: 'N° contrat',

  bien: 'Produit',
  bienPluriel: 'Produits',
  groupeBien: 'Catégorie',

  documentVente: 'Facture',
  documentVentePluriel: 'Factures',
  ligneDocument: 'Ligne facture',

  reglement: 'Paiement',
  reglementPluriel: 'Paiements',
  loyer: 'Montant',

  moduleFacturation: 'Module de facturation',
  gestionContrat: 'Gestion des commandes',
  gestionBiens: 'Gestion des produits',
  gestionTiers: 'Gestion des tiers',
  gestionArticles: 'Gestion des articles',
  gestionCaisse: 'Gestion caisse',
};

// ─── Configuration active ─────────────────────────────────────────────────────
// Changer ici pour basculer le contexte de toute l'application

type ConfigKey = 'agence' | 'commercial';

const activeConfig: ConfigKey = 'commercial';

const configs: Record<ConfigKey, AppLabels> = {
  agence: agenceImmobiliere,
  commercial,
};

export const labels: AppLabels = configs[activeConfig];
